import { useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useJobsStore } from "../../stores/jobs.store";
import { generatePresignedUrl, uploadFileToS3 } from "../../services/jobs.service";
import { JobTypeEnum } from "../../interfaces/apiInterfaces/enums/job.enums";
import type { UploadJobsProps } from "../../interfaces/componentInterfaces/uploadJobs";
import { useResources } from "../../hooks/useResources";
import { useToast } from "../../contexts/ToastContext";
import importIcon from "../../assets/images/file-import.svg";
import folderZipIcon from "../../assets/images/fileUploaded.svg";
import ContentHeader from "../contentHeader/ContentHeader";
import "./UploadJobs.scss";

const UploadJobs = ({ jobType = JobTypeEnum.Create }: UploadJobsProps = {}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  
  const navigate = useNavigate();
  const location = useLocation();
  const { getResourceValue } = useResources();
  const { showSuccess, showError } = useToast();
  
  // Get jobId from navigation state (for re-upload from JobDetails)
  const reUploadJobId = (location.state as { jobId?: string })?.jobId;
  
  const {
    isUploading,
    uploadProgress,
    uploadError,
    setUploading,
    setUploadProgress,
    setUploadError,
    resetUploadState,
    refreshJobs,
  } = useJobsStore();

  const handleFileSelect = (file: File) => {
    if (file && file.name.endsWith(".zip")) {
      setSelectedFile(file);
    } else {
      showError(
        getResourceValue("invalid_file_format", "Invalid File Format"),
        getResourceValue("please_select_zip", "Please select a ZIP file")
      );
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleStartUploading = async () => {
    if (!selectedFile) {
      showError(
        getResourceValue("error", "Error"),
        getResourceValue("please_select_file", "Please select a file first")
      );
      return;
    }

    try {
      // Reset previous upload state
      resetUploadState();
      setUploading(true);

      // Step 1: Generate presigned URL
      // Pass jobId if this is a re-upload (from JobDetails)
      const presignedData = await generatePresignedUrl(selectedFile.name, jobType, reUploadJobId);

      // Step 2: Create AbortController for cancellation
      abortControllerRef.current = new AbortController();

      // Step 3: Upload file to S3 with metadata headers
      await uploadFileToS3(
        presignedData.upload_url,
        selectedFile,
        presignedData['x-amz-meta-original-filename'],
        presignedData['x-amz-meta-user-id'],
        jobType,
        (progress) => {
          setUploadProgress(progress);
        },
        abortControllerRef.current.signal
      );

      // Step 4: Upload successful - refresh jobs, show toast, and navigate
      await refreshJobs();
      setUploading(false);
      navigate('/');
      
      // Show success toast after navigation
      showSuccess(
        getResourceValue("job_uploaded_successfully","Job Uploaded Successfully"),
        getResourceValue("job_upload_processing","Your job has been uploaded. Processing may take a few moments.")
      );

    } catch (error) {
      setUploading(false);
      // Handle cancellation vs error
      if (error instanceof Error && error.name === 'CanceledError') {
        setUploadError(getResourceValue("upload_cancelled","Upload cancelled"));
      } else {
        const errorMessage = error instanceof Error ? error.message : getResourceValue("upload_failed","Failed to upload file");
        setUploadError(errorMessage);
      }
      
      console.error('Upload failed:', error);
    } finally {
      abortControllerRef.current = null;
    }
  };

  const handleCancelUpload = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      setUploading(false);
      setUploadProgress(0);
      setUploadError(getResourceValue("upload_cancelled", "Upload cancelled"));
    }
  };

  const handleDropzoneClick = () => {
    if (!selectedFile) {
      document.getElementById("file-input")?.click();
    }
  };

  const handleCancelFile = () => {
    setSelectedFile(null);
    resetUploadState();
    // Reset file input
    const fileInput = document.getElementById("file-input") as HTMLInputElement;
    if (fileInput) {
      fileInput.value = "";
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  return (
    <div className="upload-jobs">
      <div className="upload-jobs__container">
        <ContentHeader
          title={
            reUploadJobId
              ? `${getResourceValue("re_upload", "Re-Upload")} - ${reUploadJobId}`
              : getResourceValue("upload_jobs", "Upload Jobs")
          }
          headingLevel={2}
          showActions={false}
          className="content-header--upload-jobs"
        />


        <div
          className={`upload-jobs__dropzone ${isDragging ? "dragging" : ""}`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={handleDropzoneClick}
          style={{ cursor: selectedFile ? "default" : "pointer" }}
        >
          <input
            id="file-input"
            type="file"
            accept=".zip"
            onChange={handleFileInputChange}
            style={{ display: "none" }}
          />

          <div className="upload-jobs__dropzone-content">
            <div className="upload-jobs__icon-wrapper">
              <img src={importIcon} alt="import-icon" />
            </div>

            <p className="upload-jobs__main-text">{getResourceValue("import_job_file", "Import your job file")}</p>
            <p className="upload-jobs__sub-text">{getResourceValue("supported_format_zip", "Supported file format: .ZIP")}</p>
          </div>
        </div>

        {selectedFile && (
          <div className="upload-jobs__imported-section">
            <h3 className="upload-jobs__imported-title">{getResourceValue("imported_file", "Imported File")}</h3>
            <div className="upload-jobs__file-card">
              <div className="upload-jobs__file-info">
                <div className="upload-jobs__file-icon-wrapper">
                  <img src={folderZipIcon} alt="folder-zip-icon" />{" "}
                </div>
                <div className="upload-jobs__file-details">
                  <p className="upload-jobs__file-name">{selectedFile.name}</p>
                  <p className="upload-jobs__file-meta">
                    ZIP • {formatFileSize(selectedFile.size)}
                  </p>
                </div>
              </div>
              <button
                className={`upload-jobs__remove-btn ${isUploading ? 'upload-jobs__remove-btn--disabled' : ''}`}
                onClick={handleCancelFile}
                type="button"
                disabled={isUploading}
              >
                <i className="f-icon trash-icon"></i>
                {getResourceValue("remove", "Remove")}
              </button>
            </div>

            {isUploading && (
              <div className="upload-jobs__progress-section">
                <p className="upload-jobs__progress-label">{getResourceValue("uploading", "Uploading...")}</p>
                <p className="upload-jobs__progress-percentage">{uploadProgress}%</p>
                <div className="upload-jobs__progress-bar-wrapper">
                  <div className="upload-jobs__progress-bar-background"></div>
                  <div
                    className="upload-jobs__progress-bar-fill"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
              </div>
            )}

            {uploadError && !isUploading && (
              <div className="upload-jobs__error">
                <i className="f-icon error-icon"></i>
                <span>{uploadError}</span>
              </div>
            )}
          </div>
        )}

        <div className="upload-jobs__actions">
          {isUploading ? (
            <button
              className="btn btn-red upload-jobs__cancel-upload-btn"
              onClick={handleCancelUpload}
              type="button"
            >
              <i className="f-icon cancel-icon"></i>
              <span>{getResourceValue("cancel_uploading", "Cancel Uploading")}</span>
            </button>
          ) : (
            <button
              className="btn btn-green upload-jobs__upload-btn"
              onClick={handleStartUploading}
              disabled={!selectedFile}
            >
              <i className="f-icon upload-icon"></i>
              {getResourceValue("start_uploading", "Start Uploading")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default UploadJobs;
